import React, { useState } from 'react';
import { Button, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { ResponsiveDialog } from '@/components/ResponsiveDialog';

export interface KebabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void | Promise<unknown>;
  danger?: boolean;
  /** Подтверждение перед действием (для destructive обязательно) */
  confirm?: { title: string; text?: string };
  hidden?: boolean;
}

/** navigator.clipboard есть только в secure context (https/localhost) — иначе копируем через textarea. */
export const copyText = async (text: string): Promise<void> => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!ok) throw new Error('copy failed');
};

export const shareLink = async (title: string, url = window.location.href): Promise<'shared' | 'copied'> => {
  if (navigator.share) {
    await navigator.share({ title, url });
    return 'shared';
  }
  await copyText(url);
  return 'copied';
};

export const KebabMenu: React.FC<{ items: KebabItem[]; label?: string }> = ({ items, label = 'Ещё действия' }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [pending, setPending] = useState<KebabItem | null>(null);
  const [busy, setBusy] = useState(false);
  const visible = items.filter((item) => !item.hidden);
  if (visible.length === 0) return null;

  const runItem = async (item: KebabItem) => {
    setBusy(true);
    try {
      await item.onClick();
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <>
      <IconButton aria-label={label} onClick={(e) => setAnchor(e.currentTarget)} sx={{ width: 48, height: 48 }}>
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {visible.map((item) => (
          <MenuItem
            key={item.id}
            sx={{ minHeight: 48, color: item.danger ? 'error.main' : undefined }}
            onClick={() => {
              setAnchor(null);
              if (item.confirm) setPending(item);
              else void runItem(item);
            }}
          >
            {item.icon && <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
      <ResponsiveDialog
        open={Boolean(pending)}
        onClose={() => !busy && setPending(null)}
        title={pending?.confirm?.title}
        maxWidth="xs"
        actions={
          <>
            <Button onClick={() => setPending(null)} disabled={busy} sx={{ minHeight: 48 }}>
              Отмена
            </Button>
            <Button
              variant="contained"
              color={pending?.danger ? 'error' : 'manage'}
              disabled={busy}
              onClick={() => pending && void runItem(pending)}
              sx={{ minHeight: 48 }}
            >
              {pending?.label}
            </Button>
          </>
        }
      >
        {pending?.confirm?.text && <Typography color="text.secondary">{pending.confirm.text}</Typography>}
      </ResponsiveDialog>
    </>
  );
};
